import { useDroppable } from "@dnd-kit/core";
import type {
  DraggableAttributes,
  DraggableSyntheticListeners,
} from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { cn } from "../../shared/lib/cn";
import type { PlayerListItem } from "../../shared/types/contracts";
import type { LineupContainer } from "./lineupEditorLogic";

interface LineupRow {
  id: string;
  orderIndex: number | null;
}

interface LineupZoneProps {
  zoneId: LineupContainer;
  setZoneRef: (node: HTMLDivElement | null) => void;
  title: string;
  count: number;
  emptyLabel: string;
  rows: LineupRow[];
  players: PlayerListItem[];
  outSet: Set<string>;
  tone: LineupContainer;
  readOnly: boolean;
}

export function LineupZone({
  zoneId,
  setZoneRef,
  title,
  count,
  emptyLabel,
  rows,
  players,
  outSet,
  tone,
  readOnly,
}: LineupZoneProps) {
  const isLineup = tone === "lineup";
  return (
    <div
      ref={setZoneRef}
      data-testid={`lineup-zone-${zoneId}`}
      aria-label={`${title} (${count})`}
      className="rounded-[var(--radius-sm)]"
    >
      <div
        className={cn(
          "my-1 px-1 font-mono text-[0.68rem] font-bold tracking-[0.12em]",
          isLineup ? "text-warning" : "text-foreground-muted",
        )}
      >
        {title} ({count})
      </div>
      <ZoneDropTarget zoneId={zoneId}>
        {rows.length === 0 ? (
          <div
            data-testid={`lineup-zone-${zoneId}-empty`}
            className="rounded-[var(--radius-sm)] border border-dashed border-border bg-background/45 px-3 py-3 text-xs text-foreground-muted"
          >
            {emptyLabel}
          </div>
        ) : (
          rows.map((row) => {
            const player = players.find((candidate) => candidate.id === row.id);
            if (!player) return null;
            return (
              <SortableRow
                key={row.id}
                id={row.id}
                player={player}
                orderIndex={row.orderIndex}
                inLineup={isLineup}
                isOut={outSet.has(row.id)}
                readOnly={readOnly}
              />
            );
          })
        )}
      </ZoneDropTarget>
    </div>
  );
}

function ZoneDropTarget({
  zoneId,
  children,
}: {
  zoneId: LineupContainer;
  children: ReactNode;
}) {
  const { setNodeRef } = useDroppable({ id: `${zoneId}-dropzone` });
  return (
    <div ref={setNodeRef} data-testid={`lineup-zone-${zoneId}-body`}>
      {children}
    </div>
  );
}

interface SortableRowProps {
  id: string;
  player: PlayerListItem;
  orderIndex: number | null;
  inLineup: boolean;
  isOut: boolean;
  readOnly: boolean;
}

function SortableRow({
  id,
  player,
  orderIndex,
  inLineup,
  isOut,
  readOnly,
}: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: readOnly });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : isOut ? 0.55 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`lineup-row-${id}`}
      data-player-name={player.name}
    >
      <LineupRowCard
        player={player}
        orderIndex={orderIndex}
        inLineup={inLineup}
        isOut={isOut}
        dragAttributes={readOnly ? null : attributes}
        dragListeners={readOnly ? null : listeners}
        readOnly={readOnly}
      />
    </div>
  );
}

interface LineupRowCardProps {
  player: PlayerListItem;
  orderIndex: number | null;
  inLineup: boolean;
  isOut: boolean;
  dragOverlay?: boolean;
  dragAttributes?: DraggableAttributes | null;
  dragListeners?: DraggableSyntheticListeners | null;
  readOnly: boolean;
}

export function LineupRowCard({
  player,
  orderIndex,
  inLineup,
  isOut,
  dragOverlay = false,
  dragAttributes,
  dragListeners,
  readOnly,
}: LineupRowCardProps) {
  return (
    <div
      className={cn(
        "flex min-h-10 items-center gap-2 rounded-[var(--radius-sm)] border px-1.5 py-1 text-foreground",
        inLineup
          ? "border-border bg-background/70"
          : "border-transparent bg-background/30",
        dragOverlay &&
          "border-2 border-warning bg-surface-raised shadow-[var(--shadow-overlay)]",
      )}
    >
      <span
        data-testid={inLineup ? `lineup-order-${player.id}` : undefined}
        data-player-name={player.name}
        className={cn(
          "w-6 shrink-0 text-right font-mono text-base",
          inLineup ? "text-warning" : "text-foreground-muted/60",
        )}
      >
        {inLineup ? orderIndex : "—"}
      </span>
      {readOnly ? null : (
        <button
          type="button"
          aria-label={`Drag ${player.name}`}
          data-testid={`lineup-drag-${player.id}`}
          {...(dragAttributes || {})}
          {...(dragListeners || {})}
          className={cn(
            "inline-flex size-11 shrink-0 touch-none select-none items-center justify-center rounded-[var(--radius-sm)] border border-border bg-transparent text-foreground-muted outline-none focus-visible:ring-2 focus-visible:ring-ring/70",
            dragOverlay ? "cursor-grabbing" : "cursor-grab",
            isOut && "border-border/50 text-disabled",
          )}
        >
          <GripVertical className="size-5" aria-hidden="true" />
        </button>
      )}
      <span
        className={cn(
          "min-w-0 flex-1 truncate text-base font-bold",
          isOut && "text-disabled",
        )}
      >
        <span>{player.name}</span>
        {player.num != null && (
          <span className="font-mono text-xs font-normal text-foreground-muted">
            {" "}
            #{player.num}
          </span>
        )}
      </span>
      {isOut && (
        <span
          data-testid={`lineup-out-${player.id}`}
          className="shrink-0 rounded-full border border-border/60 px-1.5 py-0.5 font-mono text-[0.62rem] font-bold tracking-wide text-disabled"
        >
          OUT
        </span>
      )}
    </div>
  );
}
